<template>
  <div class="static-page">
    <div class="static-inner">
      <router-link to="/" class="back-btn">← Back to Home</router-link>

      <article class="static-content">
        <h1>{{ page.title }}</h1>
        <div v-html="page.content" />
      </article>
    </div>
  </div>
</template>

<script>
const PAGES = {
  about: {
    title: 'About Us',
    content: `
      <p>SignalRoom (signalroom.vip) is an independent educational portal for psychometric, cognitive, and personal-development self-assessment. We offer 64 research-informed instruments across eight dimensions: intelligence, personality, career, finance, relationships, language, reasoning, and general culture.</p>
      <h3>Our Mission</h3>
      <p>Make structured self-assessment tools accessible to everyone — helping you understand your strengths, blind spots, and growth path through clear questions and careful interpretation.</p>
      <h3>Academic Standards</h3>
      <p>Each assessment is independently created by SignalRoom and inspired by published psychometric research, public educational frameworks, and well-known constructs such as Big Five traits, Holland RIASEC interests, and critical-thinking appraisal methods. Reports are educational and are not clinical diagnoses, official admissions tests, professional certifications, or employment screening decisions.</p>
      <h3>Independence and Third-Party Names</h3>
      <p>SignalRoom is not affiliated with, sponsored by, certified by, or endorsed by any third-party school, publisher, testing organization, professional association, media company, or employer referenced on the site. Third-party names are used only to identify study domains, public references, or comparative educational themes.</p>
      <h3>Contact</h3>
      <p>Questions, feedback, or partnerships: <a href="mailto:tudouchenxiaoze@gmail.com">tudouchenxiaoze@gmail.com</a></p>
    `
  },
  privacy: {
    title: 'Privacy Policy',
    content: `
      <p><em>Effective date: June 22, 2026 · Last updated: June 22, 2026</em></p>
      <p>SignalRoom ("we," "us," or "our") operates signalroom.vip, a psychometric assessment portal. This Privacy Policy explains what information we collect, how we use it, how we protect it, and your rights regarding your personal data. By using our Service, you consent to the practices described here.</p>

      <h3>1. Information We Collect</h3>
      <h4>1.1 Information You Provide</h4>
      <ul>
        <li><strong>Assessment responses:</strong> Answers you select during quizzes, used solely to generate your personalized report.</li>
        <li><strong>Contact information:</strong> Email address or other details you voluntarily submit when contacting us.</li>
        <li><strong>Communications:</strong> Content of messages you send to our support team.</li>
      </ul>
      <h4>1.2 Information Collected Automatically</h4>
      <ul>
        <li><strong>Device and browser data:</strong> IP address, browser type, operating system, device identifiers, and screen resolution.</li>
        <li><strong>Usage data:</strong> Pages visited, assessments started or completed, timestamps, referring URLs, and session duration.</li>
        <li><strong>Cookies and similar technologies:</strong> See Section 6 below.</li>
      </ul>
      <p>We do not require account registration to use assessments. We do not knowingly collect data from children under 16.</p>

      <h3>2. How We Use Your Information</h3>
      <p>We use collected information for the following purposes:</p>
      <ul>
        <li>Generate and display your personalized assessment report and radar profile</li>
        <li>Operate, maintain, and improve the platform and user experience</li>
        <li>Monitor usage trends and diagnose technical issues</li>
        <li>Respond to inquiries, feedback, and support requests</li>
        <li>Detect, prevent, and address fraud, abuse, or security incidents</li>
        <li>Comply with applicable legal obligations</li>
      </ul>
      <p><strong>We do not sell, rent, or trade your assessment responses or personal information to third parties for marketing purposes.</strong></p>

      <h3>3. Legal Basis for Processing (EEA/UK Users)</h3>
      <p>If you are located in the European Economic Area or United Kingdom, we process personal data under the following legal bases:</p>
      <ul>
        <li><strong>Consent:</strong> When you voluntarily take an assessment or contact us</li>
        <li><strong>Legitimate interests:</strong> Platform security, analytics, and service improvement</li>
        <li><strong>Legal obligation:</strong> When required by applicable law</li>
      </ul>

      <h3>4. Data Retention</h3>
      <ul>
        <li><strong>Assessment responses:</strong> Processed in real time to generate your report. We do not permanently store individual answer records after report delivery unless required for technical debugging (maximum 30 days).</li>
        <li><strong>Server logs:</strong> Retained for up to 90 days for security and analytics, then deleted or anonymized.</li>
        <li><strong>Support communications:</strong> Retained for up to 2 years or until the inquiry is resolved, whichever is longer.</li>
      </ul>

      <h3>5. Data Sharing and Disclosure</h3>
      <p>We may share information only in these limited circumstances:</p>
      <ul>
        <li><strong>Service providers:</strong> Hosting, CDN, and infrastructure partners bound by confidentiality agreements (e.g., cloud hosting providers)</li>
        <li><strong>Legal requirements:</strong> When compelled by court order, subpoena, or government request</li>
        <li><strong>Protection of rights:</strong> To protect the safety, rights, or property of SignalRoom, our users, or the public</li>
        <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, with notice to users</li>
      </ul>
      <p>We do not share assessment data with advertisers or data brokers.</p>

      <h3>6. Cookies and Tracking Technologies</h3>
      <p>We use cookies and similar technologies to:</p>
      <ul>
        <li>Maintain session state during assessments</li>
        <li>Remember preferences (e.g., language settings)</li>
        <li>Analyze aggregate traffic patterns via analytics tools</li>
      </ul>
      <p>You can control cookies through your browser settings. Disabling cookies may affect certain site functionality. Advertising cookies, if enabled, are handled according to this policy, your consent choices where required, and applicable Google advertising controls.</p>

      <h3>6.1 Advertising, Cookies, and Google AdSense</h3>
      <p>We may use Google AdSense or other Google advertising products to display ads on SignalRoom. When Google ads are enabled, Google and other third-party vendors may place or read cookies on your browser, use web beacons, IP addresses, device identifiers, or similar technologies, and collect information as a result of ad serving on this website.</p>
      <p>To understand how Google uses data from sites and apps that use Google services, visit <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener">How Google uses information from sites or apps that use our services</a>.</p>
      <ul>
        <li>We do not send assessment answers, contact messages, email addresses, names, or other information that directly identifies you to Google in ad requests.</li>
        <li>We do not use assessment responses to create advertising audiences based on sensitive personal information.</li>
        <li>Where legally required, including for EEA/UK users, we will request consent before using personalized ads or non-essential advertising cookies.</li>
        <li>You can manage ad personalization through Google ad settings and your browser privacy controls.</li>
      </ul>

      <h3>7. Data Security</h3>
      <p>We implement industry-standard safeguards including:</p>
      <ul>
        <li>HTTPS/TLS encryption for all data in transit</li>
        <li>Secure database access with restricted credentials</li>
        <li>Regular security reviews of our infrastructure</li>
        <li>Principle of least-privilege access for administrators</li>
      </ul>
      <p>No method of transmission over the Internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.</p>

      <h3>8. International Data Transfers</h3>
      <p>Your information may be processed on servers located outside your country of residence. Where required, we implement appropriate safeguards (such as Standard Contractual Clauses) for cross-border transfers.</p>

      <h3>9. Your Rights</h3>
      <p>Depending on your jurisdiction, you may have the right to:</p>
      <ul>
        <li><strong>Access:</strong> Request a copy of personal data we hold about you</li>
        <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
        <li><strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
        <li><strong>Restriction:</strong> Request limitation of processing in certain circumstances</li>
        <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
        <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
        <li><strong>Withdraw consent:</strong> Where processing is consent-based, withdraw at any time</li>
      </ul>
      <p>To exercise these rights, contact us at <a href="mailto:tudouchenxiaoze@gmail.com">tudouchenxiaoze@gmail.com</a>. We will respond within 30 days. EEA/UK residents may also lodge a complaint with their local data protection authority.</p>

      <h3>10. California Privacy Rights (CCPA/CPRA)</h3>
      <p>California residents have additional rights including the right to know what personal information is collected, request deletion, and opt out of the sale of personal information. <strong>We do not sell personal information.</strong> To submit a CCPA request, email us with "California Privacy Request" in the subject line.</p>

      <h3>11. Third-Party Links</h3>
      <p>Our site may contain links to external websites. We are not responsible for the privacy practices of third-party sites. We encourage you to review their privacy policies.</p>

      <h3>12. Changes to This Policy</h3>
      <p>We may update this Privacy Policy periodically. Material changes will be posted on this page with an updated effective date. Continued use after changes constitutes acceptance.</p>

      <h3>13. Contact Us</h3>
      <p>Privacy inquiries: <a href="mailto:tudouchenxiaoze@gmail.com">tudouchenxiaoze@gmail.com</a><br>
      Website: <a href="https://signalroom.vip">signalroom.vip</a></p>
    `
  },
  terms: {
    title: 'Terms of Service',
    content: `
      <p><em>Effective date: June 22, 2026 · Last updated: June 22, 2026</em></p>
      <p>Welcome to SignalRoom (signalroom.vip). These Terms of Service ("Terms") govern your access to and use of our website, assessments, reports, and related services (collectively, the "Service"). Please read these Terms carefully before using the Service.</p>

      <h3>1. Acceptance of Terms</h3>
      <p>By accessing or using SignalRoom, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, do not use the Service. If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization.</p>

      <h3>2. Description of Service</h3>
      <p>SignalRoom provides online psychometric and cognitive assessments for personal self-discovery and educational purposes. The Service includes:</p>
      <ul>
        <li>64 standardized assessments across eight psychological dimensions</li>
        <li>Structured scoring and multi-dimensional radar profiles</li>
        <li>Personalized interpretive reports</li>
        <li>Informational content about psychometric science</li>
      </ul>
      <p><strong>Important:</strong> All assessments are for self-discovery and educational purposes only. They do <strong>not</strong> constitute medical advice, clinical diagnosis, psychological evaluation, or professional counseling. Results should not be used as the sole basis for employment, academic, legal, or medical decisions.</p>
      <p>SignalRoom is independently operated. References to third-party organizations, schools, publishers, tests, or public frameworks do not mean that those third parties sponsor, certify, approve, or endorse SignalRoom or any SignalRoom assessment.</p>

      <h3>3. Eligibility</h3>
      <p>You must be at least 16 years old to use the Service. By using SignalRoom, you represent that you meet this age requirement. If you are under 18, you should use the Service with parental or guardian awareness.</p>

      <h3>4. User Conduct</h3>
      <p>You agree to:</p>
      <ul>
        <li>Answer assessment questions honestly and independently, without assistance from others or external resources</li>
        <li>Use the Service only for lawful, personal, non-commercial purposes</li>
        <li>Not attempt to reverse-engineer, scrape, or automated-access the platform</li>
        <li>Not manipulate, falsify, or artificially inflate assessment scores</li>
        <li>Not upload malicious code or interfere with Service operation</li>
        <li>Not impersonate others or misrepresent your affiliation</li>
        <li>Not reproduce, redistribute, or commercially exploit assessment content without written permission</li>
      </ul>

      <h3>5. Intellectual Property</h3>
      <p>All content on SignalRoom—including text, graphics, logos, assessment items, report templates, software, and design—is owned by SignalRoom or its licensors and protected by copyright, trademark, and other intellectual property laws.</p>
      <ul>
        <li>You receive a limited, non-exclusive, non-transferable license to use the Service for personal purposes</li>
        <li>Referenced psychometric frameworks (e.g., NEO-PI-R, RIASEC, Watson-Glaser) remain the property of their respective copyright holders</li>
        <li>Your personalized report is for your personal use; redistribution requires our written consent</li>
      </ul>

      <h3>6. Assessment Results and Disclaimers</h3>
      <p>You acknowledge and agree that:</p>
      <ul>
        <li>Assessment results reflect your responses at a single point in time and may vary upon retesting</li>
        <li>Reports are generated algorithmically and provide directional guidance, not definitive labels</li>
        <li>SignalRoom does not guarantee the accuracy, completeness, or fitness of results for any particular purpose</li>
        <li>We are not liable for decisions you make based on assessment results</li>
        <li>For clinical, legal, or high-stakes decisions, consult a licensed professional</li>
      </ul>

      <h3>7. Disclaimer of Warranties</h3>
      <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ACCURACY OF RESULTS. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.</p>

      <h3>8. Limitation of Liability</h3>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SIGNALROOM AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR USD $50, WHICHEVER IS GREATER.</p>

      <h3>9. Indemnification</h3>
      <p>You agree to indemnify and hold harmless SignalRoom from any claims, damages, losses, or expenses (including reasonable legal fees) arising from your violation of these Terms, misuse of the Service, or infringement of third-party rights.</p>

      <h3>10. Privacy</h3>
      <p>Your use of the Service is also governed by our <router-link to="/privacy">Privacy Policy</router-link>, which describes how we collect, use, and protect your information.</p>

      <h3>11. Modifications to the Service and Terms</h3>
      <p>We reserve the right to modify, suspend, or discontinue any part of the Service at any time without notice. We may update these Terms periodically; material changes will be posted with an updated date. Continued use after changes constitutes acceptance of the revised Terms.</p>

      <h3>12. Termination</h3>
      <p>We may terminate or restrict your access to the Service at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users or the Service. Upon termination, provisions that by their nature should survive (including intellectual property, disclaimers, and limitation of liability) will remain in effect.</p>

      <h3>13. Governing Law and Disputes</h3>
      <p>These Terms are governed by applicable international commercial law principles. Any dispute arising from these Terms or the Service shall first be attempted to be resolved through good-faith negotiation. If unresolved within 30 days, disputes may be submitted to binding arbitration or the courts of competent jurisdiction as applicable in your region.</p>

      <h3>14. Severability</h3>
      <p>If any provision of these Terms is found unenforceable, the remaining provisions will continue in full force and effect.</p>

      <h3>15. Entire Agreement</h3>
      <p>These Terms, together with the Privacy Policy, constitute the entire agreement between you and SignalRoom regarding the Service and supersede all prior agreements.</p>

      <h3>16. Contact</h3>
      <p>Questions about these Terms: <a href="mailto:tudouchenxiaoze@gmail.com">tudouchenxiaoze@gmail.com</a><br>
      Website: <a href="https://signalroom.vip">signalroom.vip</a></p>
    `
  }
}

export default {
  name: 'StaticPage',
  props: {
    type: { type: String, required: true }
  },
  computed: {
    page () {
      return PAGES[this.type] || PAGES.about
    }
  }
}
</script>

<style scoped>
.static-page {
  background: #fff;
  min-height: 80vh;
  padding: 2rem 0 4rem;
}

.static-inner {
  max-width: 820px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.back-btn {
  display: inline-block;
  color: #6b7fa0;
  font-size: 0.875rem;
  text-decoration: none;
  margin-bottom: 2rem;
}

.back-btn:hover { color: #1a3a6b; }

.static-content h1 {
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  color: #0d1f3c;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 3px solid #e8c547;
}

.static-content >>> h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.1rem;
  color: #1a3a6b;
  margin: 1.75rem 0 0.75rem;
}

.static-content >>> h4 {
  font-size: 0.95rem;
  color: #0d1f3c;
  margin: 1.25rem 0 0.5rem;
  font-weight: 600;
}

.static-content >>> ul {
  margin: 0.5rem 0 1rem 1.25rem;
}

.static-content >>> li {
  font-family: 'Source Serif 4', serif;
  font-size: 0.92rem;
  color: #4a6080;
  line-height: 1.75;
  margin-bottom: 0.35rem;
}

.static-content >>> p {
  font-family: 'Source Serif 4', serif;
  font-size: 0.95rem;
  color: #4a6080;
  line-height: 1.85;
  margin-bottom: 1rem;
}

.static-content >>> a {
  color: #1a3a6b;
  font-weight: 600;
}

.static-content >>> em {
  color: #6b7fa0;
  font-size: 0.875rem;
}

.static-content >>> strong {
  color: #0d1f3c;
}
</style>
